<template>
  <ui-modal-layout :title="tr('Deactivate brand')" :icon="PowerOffIcon" tone="warning">
    <div class="flex flex-col gap-3 text-muted-foreground">
      <p class="m-0">{{ tr('While the ":brandName" brand is inactive, its automations do not run and no weekly report is sent about it.', { ':brandName': brand.name }) }}</p>
      <p class="m-0">{{ tr('An inactive brand does not count towards the brand limit of your subscription. In the current billing period you can reactivate it, or use its place for another brand, without any extra cost.') }}</p>
      <p class="m-0">{{ tr('If you reactivate it in a later billing period and you have no free brand place left, the extra brand fee applies, just like when creating a new brand.') }}</p>
    </div>

    <label :class="[ 'flex items-center gap-2.5 text-sm text-foreground', isLoading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer' ]">
      <ui-checkbox v-model="pauseCampaigns" :disabled="isLoading" />
      {{ tr('Pause the running campaigns of the brand\'s automations') }}
    </label>

    <info-text>
      <template v-if="pauseCampaigns">
        {{ tr('The paused campaigns are restarted automatically when you reactivate the brand.') }}
      </template>
      <template v-else>
        {{ tr('The campaigns keep running and spending, but no automation manages them while the brand is inactive.') }}
      </template>
    </info-text>

    <template #footer>
      <ui-button variant="outline" :disabled="isLoading" @click="() => close()">
        {{ tr('Cancel') }}
      </ui-button>
      <ui-button :disabled="isLoading" @click="() => confirm()">
        <loader-circle-icon v-if="isLoading" class="animate-spin" />
        {{ tr('Deactivate') }}
      </ui-button>
    </template>
  </ui-modal-layout>
</template>

<script>
import { LoaderCircle, PowerOff } from '@lucide/vue';
import { markRaw } from 'vue';

import Brand from '@/entities/Brand.js';
import InfoText from '@/ui/components/InfoText.vue';

export default {
  name: 'BrandDeactivateModalContent',
  components: {
    InfoText,
    LoaderCircleIcon: LoaderCircle,
  },
  props: {
    brand: {
      type: Brand,
      required: true,
    },
    // Receives the "pause campaigns" choice, returns a Promise.
    confirmCallback: {
      type: Function,
      required: true,
    },
  },
  emits: [ 'close' ],
  data: () => ({
    PowerOffIcon: markRaw(PowerOff),
    isLoading: false,
    pauseCampaigns: true,
  }),
  methods: {
    close() {
      this.$emit('close');
    },
    confirm() {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;

      // Pausing is a no-op on the backend when the brand has no running campaigns.
      this.confirmCallback(this.pauseCampaigns)
        .then(() => this.close())
        // The error toast is shown by the callback, the modal stays open for another try.
        .catch(() => null)
        .finally(() => this.isLoading = false);
    },
  },
};
</script>
